import { useCallback, useEffect, useRef, useState } from "react";
import { api, wsUrl } from "@/lib/api";
import {
  batchFromLiveData,
  planSequenceSigns,
  sequenceEntity,
  sequenceKey,
  sequenceLabel,
  sequenceStartingStatement,
} from "@/lib/announcerSequence";
import { mergeLiveCurrent, normalizeDisplayCount, subscribeLiveCurrent, subscribeLocalAnnouncerPing, subscribeSequenceAction } from "@/lib/liveBroadcast";

const LOGO = "/mu-logo.png";
const RECONNECT_MS = 1500;
const FALLBACK_POLL_MS = 2000;

function studentKey(st) {
  return st.id ?? st.registration_id ?? st.htno;
}

function announcerNameParts(name) {
  return (name || "").trim().split(/\s+/).filter(Boolean);
}

function AnnouncerName({ name, className, as: Tag = "span", ...rest }) {
  const parts = announcerNameParts(name);
  if (!parts.length) return null;
  return (
    <Tag
      {...rest}
      className={`inline-flex flex-wrap justify-center items-baseline gap-x-[0.45em] gap-y-[0.08em] uppercase ${className || ""}`}
    >
      {parts.map((part, idx) => (
        <span key={`${part}-${idx}`}>{part}</span>
      ))}
    </Tag>
  );
}

function SequenceSign({ variant, label, entity }) {
  const isStarting = variant === "starting";
  if (isStarting) {
    return (
      <div className="text-center max-w-[92vw]" data-testid="sequence-sign-starting">
        <p
          className="font-serif font-semibold text-[clamp(1.5rem,3.8vw,4rem)] leading-[1.4] tracking-[0.02em] text-white max-w-[86vw] mx-auto"
          data-testid="sequence-starting-statement"
        >
          {label}
        </p>
      </div>
    );
  }
  return (
    <div className="text-center max-w-[92vw]" data-testid={`sequence-sign-${variant}`}>
      <p className="font-serif font-black uppercase leading-none text-amber-200 text-[clamp(2.5rem,5.5vw,6rem)] tracking-[0.14em]">
        Sequence Completed
      </p>
      {entity && (
        <p className="text-[clamp(1rem,2.2vw,2.5rem)] tracking-[0.32em] uppercase text-white/60 font-medium mt-12">
          {entity}
        </p>
      )}
      <h1 className="font-serif font-black text-[clamp(2rem,6.5vw,7rem)] mt-8 leading-[1.05] uppercase tracking-[0.08em] text-white">
        {label}
      </h1>
    </div>
  );
}

function SingleName({ student }) {
  const isPriority = student?.queue_type === "PRIORITY";
  return (
    <div key={studentKey(student)} className="w-full">
      {isPriority && (
        <p className="text-[1.4vw] tracking-[0.4em] uppercase text-amber-400 font-semibold mb-2">
          ★ Gold Medalist ★
        </p>
      )}
      <p className="text-[2.2vw] tracking-[0.35em] uppercase text-white/60 font-medium">
        {student.entity}
      </p>
      <p className="text-[2vw] tracking-[0.15em] uppercase text-white font-bold mt-3">
        {student.program} · {student.specialization}
      </p>
      <AnnouncerName
        name={student.student_name}
        as="h1"
        className="font-serif font-black text-[9.5vw] leading-[1.08] mt-10"
        data-testid="announcer-name"
      />
    </div>
  );
}

function batchProgramSpecialization(batch) {
  if (!batch.length) return null;
  const labels = batch.map((s) => {
    const program = (s.program || "").trim();
    const spec = (s.specialization || "").trim();
    if (program && spec) return `${program} · ${spec}`;
    return program || spec || "";
  }).filter(Boolean);
  const unique = [...new Set(labels)];
  if (unique.length === 1) return unique[0];
  const first = batch[0];
  const program = (first?.program || "").trim();
  const spec = (first?.specialization || "").trim();
  if (program && spec) return `${program} · ${spec}`;
  return program || spec || null;
}

function BatchNameRow({ student, compact }) {
  const isPriority = student?.queue_type === "PRIORITY";
  return (
    <div
      key={studentKey(student)}
      className={`w-full border-b border-white/10 last:border-b-0 ${
        compact ? "py-[1vh]" : "py-[1.6vh]"
      }`}
      data-testid={`announcer-slot-${studentKey(student)}`}
    >
      {isPriority && (
        <p className={`tracking-[0.25em] uppercase text-amber-400 font-semibold mb-1 ${
          compact ? "text-[0.75vw]" : "text-[0.85vw]"
        }`}
        >
          ★ Gold
        </p>
      )}
      <AnnouncerName
        name={student.student_name}
        as="h2"
        className={`font-serif font-black leading-tight ${
          compact ? "text-[3.4vw]" : "text-[5vw]"
        }`}
      />
    </div>
  );
}

function BatchStack({ batch, compact }) {
  return (
    <div
      className="w-full max-w-[88vw] flex flex-col items-stretch overflow-hidden rounded-xl border border-white/15 bg-white/[0.03]"
      data-testid="announcer-batch"
    >
      <div className={`flex flex-col px-[3vw] ${compact ? "py-[0.6vh]" : "py-[1vh]"}`}>
        {batch.map((student) => (
          <BatchNameRow key={studentKey(student)} student={student} compact={compact} />
        ))}
      </div>
    </div>
  );
}

export default function Announcer() {
  const [displayState, setDisplayState] = useState(null);
  const [sequenceSign, setSequenceSign] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const prefetchRef = useRef(null);
  const prevBatchRef = useRef([]);
  const displayStateRef = useRef(null);
  const sequenceSignRef = useRef(null);
  const pendingTransitionRef = useRef(null);
  const signProgressRef = useRef(new Set());
  const goSequenceHomeRef = useRef(() => {});
  const advanceSequenceRef = useRef(() => {});

  useEffect(() => {
    displayStateRef.current = displayState;
  }, [displayState]);

  useEffect(() => {
    sequenceSignRef.current = sequenceSign;
  }, [sequenceSign]);

  const clearPendingTransition = useCallback(() => {
    pendingTransitionRef.current = null;
    signProgressRef.current = new Set();
  }, []);

  const showNames = useCallback((data) => {
    setDisplayState(data);
    setSequenceSign(null);
    prevBatchRef.current = batchFromLiveData(data);
    clearPendingTransition();
  }, [clearPendingTransition]);

  const showSignForStep = useCallback((step, data, previousBatch) => {
    const labelBatch = step === "completing" ? previousBatch : batchFromLiveData(data);
    setSequenceSign({
      variant: step,
      label: step === "starting" ? sequenceStartingStatement(labelBatch) : sequenceLabel(labelBatch),
      entity: sequenceEntity(labelBatch),
    });
  }, []);

  const beginManualTransition = useCallback((steps, data, previousBatch) => {
    pendingTransitionRef.current = { steps, data, previousBatch };
    signProgressRef.current = new Set();
    showSignForStep(steps[0], data, previousBatch);
  }, [showSignForStep]);

  const goSequenceHome = useCallback(() => {
    const transition = pendingTransitionRef.current;
    if (!transition) return;
    showSignForStep("starting", transition.data, transition.previousBatch);
  }, [showSignForStep]);

  const advanceSequence = useCallback(() => {
    const transition = pendingTransitionRef.current;
    if (!transition) return;

    const currentSign = sequenceSignRef.current;
    if (currentSign?.variant !== "starting") return;

    signProgressRef.current.add("starting");
    showNames(transition.data);
  }, [showNames]);

  useEffect(() => {
    goSequenceHomeRef.current = goSequenceHome;
    advanceSequenceRef.current = advanceSequence;
  }, [goSequenceHome, advanceSequence]);

  useEffect(() => subscribeSequenceAction((action) => {
    if (action === "home") goSequenceHomeRef.current();
    else if (action === "resume") advanceSequenceRef.current();
  }), []);

  const ingestLiveUpdate = useCallback((data, options = {}) => {
    const previousBatch = prevBatchRef.current;
    const nextBatch = batchFromLiveData(data);

    if (!nextBatch.length) {
      if (pendingTransitionRef.current) return;
      showNames(data);
      prevBatchRef.current = [];
      return;
    }

    if (options.initialSync && !previousBatch.length) {
      showNames(data);
      return;
    }

    const steps = planSequenceSigns(previousBatch, nextBatch);

    if (!steps.length) {
      if (pendingTransitionRef.current) return;
      showNames(data);
      return;
    }

    const pending = pendingTransitionRef.current;
    if (pending) {
      const pendingNext = batchFromLiveData(pending.data);
      if (sequenceKey(pendingNext) === sequenceKey(nextBatch)) {
        pendingTransitionRef.current = {
          ...pending,
          data: mergeLiveCurrent(pending.data, data),
        };
        return;
      }
    }

    beginManualTransition(steps, data, previousBatch);
  }, [beginManualTransition, showNames]);

  const handleIncoming = useCallback((data) => {
    ingestLiveUpdate(mergeLiveCurrent(displayStateRef.current, data));
  }, [ingestLiveUpdate]);

  useEffect(() => subscribeLiveCurrent(handleIncoming), [handleIncoming]);

  useEffect(() => subscribeLocalAnnouncerPing(), []);

  useEffect(() => {
    let stopped = false;

    const connect = () => {
      const ws = new WebSocket(wsUrl("/ws/backstage"));
      wsRef.current = ws;
      ws.onopen = () => setWsConnected(true);
      ws.onmessage = (evt) => {
        try {
          const { type, data } = JSON.parse(evt.data);
          if (type === "prefetch") {
            prefetchRef.current = data;
          } else if (type === "current") {
            const prefetched = prefetchRef.current;
            const nextId = data?.current?.registration_id ?? data?.current_batch?.[0]?.registration_id;
            const prefetchedId = prefetched?.current?.registration_id ?? prefetched?.current_batch?.[0]?.registration_id;
            const payload = prefetched && nextId && nextId === prefetchedId ? prefetched : data;
            prefetchRef.current = null;
            handleIncoming(payload);
          } else if (type === "state") {
            prefetchRef.current = null;
            ingestLiveUpdate(data, { initialSync: true });
          } else if (type === "sequence_action") {
            if (data?.action === "home") goSequenceHomeRef.current();
            else if (data?.action === "resume") advanceSequenceRef.current();
          }
        } catch (_e) { /* ignore */ }
      };
      ws.onclose = () => {
        setWsConnected(false);
        if (!stopped) reconnectTimerRef.current = setTimeout(connect, RECONNECT_MS);
      };
      ws.onerror = () => { try { ws.close(); } catch (_e) { /* ignore */ } };
    };

    connect();
    return () => {
      stopped = true;
      setWsConnected(false);
      clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) { try { wsRef.current.close(); } catch (_e) { /* ignore */ } }
    };
  }, [handleIncoming, ingestLiveUpdate]);

  useEffect(() => {
    if (wsConnected) return undefined;

    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get("/announcer/current");
        if (!cancelled) ingestLiveUpdate(r.data, { initialSync: !displayStateRef.current });
      } catch (_e) { /* ignore */ }
    };

    poll();
    const id = setInterval(poll, FALLBACK_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [wsConnected, ingestLiveUpdate]);

  const live = displayState?.live;
  const displayCount = normalizeDisplayCount(live?.display_count);
  const batch = batchFromLiveData(displayState);
  const programLabel = !sequenceSign && displayCount > 1 && batch.length
    ? batchProgramSpecialization(batch)
    : null;
  return (
    <div className="relative min-h-screen w-full stage-bg grain overflow-hidden text-white" data-testid="announcer-screen">
      <div className="absolute top-8 left-10 right-10 flex items-start gap-8">
        <img src={LOGO} alt="MU" className="h-12 w-auto shrink-0 invert brightness-0 opacity-90" />
        {programLabel && (
          <div className="flex-1 flex items-center justify-center min-w-0 pt-1 px-4">
            <p
              className="font-serif text-[1.5vw] tracking-[0.42em] uppercase text-white font-black text-center leading-relaxed"
              data-testid="announcer-program-header"
            >
              {programLabel}
            </p>
          </div>
        )}
        <div className={`text-right shrink-0 ${programLabel ? "" : "ml-auto"}`}>
          <p className="text-xs tracking-[0.35em] uppercase text-white/50">
            {live?.queue_type === "LATE" ? "Late Queue" : live?.queue_type === "PRIORITY" ? "Priority" : "Convocation"}
          </p>
          <p className="text-sm text-white/70 mt-1">
            {new Date().getFullYear()}
            {!sequenceSign && displayCount > 1 && (
              <span className="ml-3 text-white/45">· {displayCount} on stage</span>
            )}
          </p>
        </div>
      </div>

      <div className="min-h-screen w-full flex flex-col items-center justify-center px-8 text-center">
        {sequenceSign ? (
          <SequenceSign
            variant={sequenceSign.variant}
            label={sequenceSign.label}
            entity={sequenceSign.entity}
          />
        ) : batch.length > 0 ? (
          displayCount === 1 ? (
            <SingleName student={batch[0]} />
          ) : displayCount === 3 ? (
            <BatchStack batch={batch} compact={false} />
          ) : (
            <BatchStack batch={batch} compact />
          )
        ) : (
          <div className="text-center">
            <p className="kicker text-white/60">Awaiting</p>
            <h1 className="font-serif text-[5vw] mt-6">Ready to Begin</h1>
            <p className="mt-4 text-white/60 text-lg">The next graduate will be announced shortly.</p>
          </div>
        )}
      </div>

      {live?.is_paused && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 px-6 py-2 border border-white/30 rounded-full text-sm tracking-[0.3em] uppercase text-white/70">
          Paused
        </div>
      )}
    </div>
  );
}
